import { MapPin, X } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import PlaceMarker, { type MapPlace } from '../PlaceMarker';

type ClickEvent = {
  stopPropagation: () => void;
};

type PlaceStackMarkerProps = {
  places: MapPlace[];
  labels: Record<string, string>;
  onOpen: (place: MapPlace) => void;
};

const radiusOffsets = [0, 10, -5, 7, -3, 12, -7, 5];
const angleOffsets = [-4, 5, -2, 4, -5, 3, -3, 5];

const getBubblePosition = (index: number, count: number) => {
  if (count === 2) {
    const angle = [-145, -35][index];
    const radius = 88 + radiusOffsets[index];
    return { angle, radius };
  }

  if (count === 3) {
    const angle = [-155, -90, -25][index];
    const radius = 91 + radiusOffsets[index];
    return { angle, radius };
  }

  const ringIndex = index % 8;
  const ring = Math.floor(index / 8);
  const ringCount = Math.min(8, count - ring * 8);
  const angle = -90 + (360 / ringCount) * ringIndex + angleOffsets[ringIndex];
  const radius = 92 + ring * 62 + radiusOffsets[ringIndex];

  return { angle, radius };
};

export default function PlaceStackMarker({
  places,
  labels,
  onOpen,
}: PlaceStackMarkerProps) {
  const [expanded, setExpanded] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const closeStack = () => {
    if (closing) return;

    setClosing(true);
    closeTimer.current = setTimeout(() => {
      setExpanded(false);
      setClosing(false);
      closeTimer.current = null;
    }, 250);
  };

  const toggleStack = () => {
    if (expanded) {
      closeStack();
      return;
    }

    setExpanded(true);
  };

  return (
    <div className="place-stack-marker">
      <button
        type="button"
        className="place-stack-trigger"
        aria-label={expanded ? labels.cancel : `${places.length} ${labels.entriesAtLocation}`}
        aria-expanded={expanded}
        onClick={(event: ClickEvent) => {
          event.stopPropagation();
          toggleStack();
        }}
      >
        {expanded ? <X weight="bold" /> : <MapPin weight="fill" />}
        {!expanded && <span>{places.length}</span>}
      </button>

      {expanded && (
        <div
          className={`place-stack-bubbles ${closing ? 'is-closing' : ''}`}
          onClick={(event: ClickEvent) => event.stopPropagation()}
        >
          {places.map((place, index) => {
            const { angle, radius } = getBubblePosition(index, places.length);
            const radians = angle * (Math.PI / 180);
            const style = {
              '--stack-angle': `${angle}deg`,
              '--stack-radius': `${radius}px`,
              '--stack-x': `${Math.cos(radians) * radius}px`,
              '--stack-y': `${Math.sin(radians) * radius}px`,
              '--stack-delay': `${index * 28}ms`,
              '--stack-exit-delay': `${(index % 4) * 16}ms`,
            };

            return (
              <div className="place-stack-entry" key={place.id} style={style}>
                <PlaceMarker
                  place={place}
                  onClick={() => {
                    closeStack();
                    onOpen(place);
                  }}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
